import { Briefcase } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { isActiveEngagementStatus } from '../utils/focusedEngagement';

export interface SwitchableEngagement {
  id: string;
  name?: string;
  title?: string;
  sowId?: string;
  status?: string;
}

interface EngagementSwitcherProps {
  engagements: ReadonlyArray<SwitchableEngagement>;
  value: string | null;
  onChange: (engagementId: string) => void;
  /** Render even when there is only one engagement (default: hide). */
  alwaysShow?: boolean;
  className?: string;
  size?: 'sm' | 'default';
}

function statusClasses(status?: string): string {
  const s = String(status ?? '').toLowerCase();
  if (isActiveEngagementStatus(s)) return 'bg-green-50 text-green-700 border-green-200';
  if (s === 'pending_approval') return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  if (s === 'draft') return 'bg-gray-50 text-gray-700 border-gray-200';
  if (s === 'completed') return 'bg-blue-50 text-blue-700 border-blue-200';
  return 'bg-slate-50 text-slate-600 border-slate-200';
}

export function engagementLabel(e: SwitchableEngagement): string {
  return e.name || e.title || e.sowId || 'Engagement';
}

/**
 * Company-header control for choosing the focused engagement (PRD §4.7).
 * Hidden when the company has fewer than two engagements unless `alwaysShow`.
 */
export function EngagementSwitcher({
  engagements,
  value,
  onChange,
  alwaysShow = false,
  className = '',
  size = 'default',
}: EngagementSwitcherProps) {
  const list = engagements.filter((e) => e && e.id);
  if (list.length === 0) return null;
  if (list.length < 2 && !alwaysShow) return null;

  const current = value ? list.find((e) => e.id === value) : undefined;

  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="engagement-switcher">
      <Briefcase className="size-4 text-muted-foreground shrink-0" aria-hidden="true" />
      <Select value={value ?? ''} onValueChange={onChange}>
        <SelectTrigger
          className={`${size === 'sm' ? 'h-8 text-sm' : ''} w-[260px] bg-white border-slate-200`}
          aria-label="Focused engagement"
        >
          <SelectValue placeholder="Select an engagement" />
        </SelectTrigger>
        <SelectContent>
          {list.map((e) => (
            <SelectItem key={e.id} value={e.id}>
              <div className="flex items-center gap-2">
                <span className="truncate max-w-[170px]">{engagementLabel(e)}</span>
                {e.status && (
                  <Badge variant="outline" className={`${statusClasses(e.status)} px-1.5 py-0 text-[10px]`}>
                    {String(e.status).replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {current?.status && (
        <Badge variant="outline" className={`${statusClasses(current.status)} hidden sm:inline-flex`}>
          {String(current.status).replace(/_/g, ' ')}
        </Badge>
      )}
    </div>
  );
}
